import React, { useMemo, useRef, useState, type ReactNode } from 'react'
import { HullLayer } from './HullLayer'
import { HullScopeContext, HullStore } from './hullScope'

export interface SelectionHullScopeProps {
  children: ReactNode
  /**
   * Die ganze Seite ruht: Abschnitte wandern per `transform` (Abschnitt ziehen), das sähe die
   * gemeinsame Ebene nicht. Die Zeilen malen ihre Markierung dann selbst.
   */
  resting?: boolean
}

/**
 * **Eine Auswahl-Hülle für alle Listen einer Seite** (#293, Sven 09.10.2026: „überall gleich“). Ist
 * jeder Tag oder Abschnitt eine eigene Liste, fuhr die Markierung nur innerhalb einer Liste und
 * blendete an der Grenze aus und ein. Hier liegt EINE Ebene vor allen Listen; jede `SelectionHull`
 * darin meldet nur ihre Zeilen. Blöcke bleiben je Liste getrennt, wandern darf eine Fläche überall hin.
 *
 * Gehört als Inhalt direkt in den Kasten aller Listen — dieser braucht `position: relative`, die Ebene
 * wird sein erstes Kind. Köpfe zwischen den Listen brauchen `position: relative`, damit ihr Text über
 * einer durchfahrenden Fläche liegt.
 *
 * @example
 * <div className="done-page__days">
 *   <SelectionHullScope>
 *     {days.map(day => <TaskList key={day.key} … />)}
 *   </SelectionHullScope>
 * </div>
 */
export const SelectionHullScope: React.FC<SelectionHullScopeProps> = ({ children, resting = false }) => {
  const [store] = useState(() => new HullStore())
  const layerRef = useRef<HTMLDivElement>(null)
  const value = useMemo(() => ({ store, resting }), [store, resting])
  return (
    <HullScopeContext.Provider value={value}>
      <HullLayer store={store} kind="task" resting={resting} layerRef={layerRef} />
      {children}
    </HullScopeContext.Provider>
  )
}
